import React, { Fragment } from "react";
import { Form, ActionBar, ActionBarItem } from "@redriver/cinnamon";
import { moveStep, saveStep } from "./actions";
import {
  getCompletedStepIndex,
  getCurrentStepKey,
  getIsConfirmed,
  getIsFormDirty,
  getIsSaving,
  getSaveCount,
} from "./selectors";
import { useDispatch, useSelector } from "react-redux";
import { Heading } from "components/containers";
import { RoundedButton } from "components/buttons";
import FormPanel from "./FormPanel";
import SubmitButton from "./SubmitButton";
import SubmitDetails from "./SubmitDetails";
import MedicalForm from "./MedicalForm";
import PassportForm from "./PassportForm";
import PersonalInfoForm from "./PersonalInfoForm";
import { RepInfoSteps } from "constants/enums";
import AdditionalInfoForm from "./AdditionalInfoForm";
import { Loader } from "semantic-ui-react";

const buildForm = (stepKey) => {
  switch (stepKey) {
    case RepInfoSteps.Personal:
      return <PersonalInfoForm />;
    case RepInfoSteps.Passport:
      return <PassportForm />;
    case RepInfoSteps.Medical:
      return <MedicalForm />;
    case RepInfoSteps.Additional:
      return <AdditionalInfoForm />;
    case RepInfoSteps.Submit:
      return <SubmitDetails />;
    default:
      return null;
  }
};

const FormStep = ({
  stepIndex,
  step,
  hasNextStep,
  hasPreviousStep,
  onNextStep,
  onPreviousStep,
  steps,
  ...props
}) => {
  const dispatch = useDispatch();
  const form = buildForm(step.key);
  const isConfirmed = useSelector(getIsConfirmed);
  const stepCompletedIndex = useSelector(getCompletedStepIndex);
  const isSavingStep = useSelector(getIsSaving);
  const isDirty = useSelector(getIsFormDirty);
  return (
    <div className="form-step">
      <Form.Object field={step.key} propagateUpdates="always">
        <div className="form-step-heading">
          <Heading>{step.name}</Heading>
          {step.description && <p>{step.description}</p>}
        </div>
        <FormPanel>
          {form}
          <ActionBar className="form-nav" verticalAlign="middle">
            <SavingItem isSaving={isSavingStep} key={isSavingStep} />

            {hasPreviousStep && (
              <ActionBar.Item>
                <RoundedButton
                  onClick={() => {
                    dispatch(
                      saveStep({
                        stepKey: step.key,
                        completedStepIndex: stepCompletedIndex,
                        isDirty,
                      })
                    );

                    dispatch(
                      moveStep({
                        targetStepKey: steps[stepIndex - 1].key,
                      })
                    );
                  }}
                  primary
                >
                  Back
                </RoundedButton>
              </ActionBar.Item>
            )}
            <ActionBar.Space />
            {hasNextStep && (
              <ActionBar.Item>
                <RoundedButton
                  onClick={() => {
                    const nextCompletedIndex =
                      stepCompletedIndex == null ||
                      stepCompletedIndex < stepIndex
                        ? stepIndex
                        : stepCompletedIndex;
                    dispatch(
                      saveStep({
                        stepKey: step.key,
                        completedStepIndex: nextCompletedIndex,
                        isDirty,
                      })
                    );

                    dispatch(
                      moveStep({
                        targetStepKey: steps[stepIndex + 1].key,
                      })
                    );
                  }}
                  positive
                >
                  Next
                </RoundedButton>
              </ActionBar.Item>
            )}
            {!hasNextStep && !isConfirmed && <SubmitButton confirm />}
          </ActionBar>
        </FormPanel>
      </Form.Object>
    </div>
  );
};

const SavingItem = ({ isSaving }) => {
  const saveCount = useSelector(getSaveCount);
  const isDirty = useSelector(getIsFormDirty);
  const currentStep = useSelector(getCurrentStepKey);

  if (currentStep == RepInfoSteps.Submit) {
    return null;
  }

  return (
    <ActionBar.Item fluid>
      {isSaving ? (
        <Fragment>
          <Loader active /> <span>Saving...</span>
        </Fragment>
      ) : !isDirty && saveCount > 0 ? (
        "Saved"
      ) : null}
    </ActionBar.Item>
  );
};

export default FormStep;
